import { usePage } from "@inertiajs/react"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import type { PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  /** One photograph per card, keyed by PageContentRegistry::TILE_SLOTS. */
  photos: Record<string, PlacementPhoto | null>
  /** Paths to leave out — the page the tiles are sitting on, so it can't link to itself. */
  exclude?: string[]
}

// Photo cards under the closing "contact me" band, working like a second menu:
// every page of her site the visitor hasn't just read. Same card as the rest of
// her site — rounded, hairline ring, photo over a label — rather than the
// full-bleed tiles with solid colour bars this used to be. Each card has its own
// photo slot, so a picture stays with the card whatever else comes and goes; a
// card without one falls back to a soft tint, so the row still looks deliberate
// with none placed. Six cards wherever you are: the page you are on drops out
// and Online Therapy takes its place.
export default function GiselaPageTiles({ photos, exclude = [] }: Props) {
  const { props } = usePage<PageProps>()
  const tn = props.translations.gisela_nav
  const lp = useLocalePath()

  // Only rendered on Gisela's host — these paths don't resolve on Sanctuary's.
  if (props.current_brand.key !== "gisela") return null

  // The reading order is the site's own: who she is, then what she treats and
  // how, then the practical pages. Each tile names its own photo slot, so the
  // row can be reordered here — or a card left out — without a picture moving.
  const shown = (tile: { path: string }) =>
    !props.hidden_paths.includes(tile.path) && !exclude.includes(tile.path)

  const tiles = [
    { label: tn.about, path: "/about", photo: "about" },
    { label: tn.help_with, path: "/what-i-can-help-with", photo: "help_with" },
    { label: tn.therapies, path: "/therapies-i-offer", photo: "therapies" },
    { label: tn.fees, path: "/fees", photo: "fees" },
    { label: tn.blog, path: "/blog", photo: "blog" },
    { label: tn.contact, path: "/contact", photo: "contact" },
  ].filter(shown)

  // Online Therapy stands in reserve rather than in the row: it is the one
  // page of hers that belongs beside any of the others, so it fills the gap
  // whenever a tile drops out — the page the visitor is already on, or one
  // switched off in the admin. The row is then six wherever you are, which is
  // what it was designed around; on a page that excludes nothing it stays out
  // and the six are the six.
  const reserve = [ { label: tn.online_therapy, path: "/online-therapy", photo: "online_therapy" } ].filter(shown)

  const row = tiles
    .concat(reserve.slice(0, Math.max(0, 6 - tiles.length)))
    .map((tile) => ({ ...tile, photo: photos[tile.photo] ?? null }))

  // Six across is what this row was designed around, and the reserve keeps it
  // there. The narrower counts still have to work: a page switched off in the
  // admin can take the row below six with nothing left to fill it.
  const columns =
    row.length >= 6
      ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
      : row.length === 5
        ? "grid-cols-2 sm:grid-cols-3 xl:grid-cols-5"
        : row.length === 4
          ? "grid-cols-2 xl:grid-cols-4"
          : row.length === 3
            ? "grid-cols-1 sm:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2"

  // Five or six in a row leaves each card about a third as wide as four did,
  // so the picture is given a shape rather than a fixed height (a 224px band
  // in a 190px column is a portrait) and the label loses a size — otherwise
  // "What I help with" wraps onto two lines in half the row.
  const dense = row.length >= 5

  // The band is tucked up under the contact band it follows and runs the full
  // width of it — the cards' outer edges meet the viewport exactly where the
  // teal above them does, rather than sitting in the 7xl column the page's
  // reading bands use — so the two read as one closing section.
  return (
    <section data-testid="page-tiles" className="bg-stone pt-3 pb-12 lg:pb-16">
      <div>
        <div className={cn("grid", dense ? "gap-4" : "gap-5", columns)}>
          {row.map((tile) => (
            <a
              key={tile.path}
              href={lp(tile.path)}
              className="group flex flex-col overflow-hidden rounded-2xl bg-white no-underline ring-1 ring-hairline transition-shadow hover:shadow-lg"
            >
              <div
                className={cn(
                  "relative overflow-hidden",
                  dense ? "aspect-[4/3]" : "h-40 sm:h-48 xl:h-56",
                )}
              >
                {tile.photo ? (
                  <img
                    src={tile.photo.card_url}
                    alt={tile.photo.alt_text ?? ""}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="h-full w-full bg-sage-light/40" />
                )}
              </div>
              <span
                className={cn(
                  "flex items-center justify-between font-semibold text-ink-display",
                  dense ? "gap-2 px-4 py-3.5 text-sm" : "gap-3 px-5 py-4 text-base",
                )}
              >
                {tile.label}
                <span aria-hidden className="text-teal transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}
